import React, { useEffect, useState } from "react";
import { Loader2, Banknote } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import AccountSelect from "@/components/AccountSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";


export default function BulkPayDialog({ open, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("Havale/EFT");
  const [accountId, setAccountId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    api.get("/staff-ledger/bulk-pay/preview").then((r) => { setPreview(r.data); setSelected(r.data.items.map((i) => i.staff_id)); });
  }, [open]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const total = (preview?.items || []).filter((i) => selected.includes(i.staff_id)).reduce((a, i) => a + i.balance, 0);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/staff-ledger/bulk-pay", { date, method, staff_ids: selected, account_id: accountId || null });
      toast.success(data.message); onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bulk-pay-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Banknote className="h-5 w-5 text-primary" /> Tüm Maaşları Öde</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Ödeme Tarihi</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="bulk-pay-date" className="font-mono" /></div>
          <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Ödendiği Hesap</Label>
            <AccountSelect value={accountId} onChange={(id, a) => { setAccountId(id); if (a) setMethod(a.name); }} testId="bulk-pay-method" /></div>
        </div>
        <div className="max-h-64 overflow-y-auto divide-y divide-border/60 rounded-lg border border-border">
          {!preview && <div className="p-4 text-center text-sm text-muted-foreground">Yükleniyor…</div>}
          {preview?.items.length === 0 && <div className="p-4 text-center text-sm text-muted-foreground" data-testid="bulk-pay-empty">Ödenecek maaş borcu olan personel yok</div>}
          {preview?.items.map((i) => (
            <label key={i.staff_id} data-testid={`bulk-pay-item-${i.staff_id}`} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-accent/40">
              <Checkbox checked={selected.includes(i.staff_id)} onCheckedChange={() => toggle(i.staff_id)} />
              <span className="flex-1"><span className="font-medium">{i.name}</span> <span className="text-xs text-muted-foreground">· {i.role}</span></span>
              <span className="font-mono font-semibold text-amber-400">{TRY(i.balance)}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-between text-sm"><span className="text-muted-foreground">{selected.length} personel seçili</span><span className="font-mono font-bold" data-testid="bulk-pay-total">{TRY(total)}</span></div>
        <p className="text-xs text-muted-foreground">Her personel için kalan borcun tamamı "Maaş Ödemesi" olarak işlenir ve PERSONEL maliyetine yansır.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={pay} disabled={busy || selected.length === 0} data-testid="bulk-pay-confirm" className="gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Ödemeyi Onayla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
